import {
  Dialog, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Typography,
  Alert, useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import ModalActionBar from 'components/modal-action-bar';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLLMProxyServiceContext } from 'contexts/llmProxySerivceContext';
import { useUserInformationContext } from 'contexts/userContext';
import { buildAssistantInstruction, formatTimezoneOffset } from 'views/imports/build-instruction';
import { ExistingOrderSearch } from 'views/imports/components/existing-order-search';
import { SendConfirmDialog } from 'views/imports/components/send-confirm-dialog';
import type { PickedOrder } from 'views/imports/components/existing-order-search';
import { agencyChoiceNeeded, NewOrderFields } from 'views/imports/components/new-order-fields';
import type { ImportTarget, MarkdownLine, ParsedMarkdown } from 'services/imports/markdownContract';

export type TargetAndHandoffProps = {
  markdown: string;
  parsed: ParsedMarkdown;
  open: boolean;
  onClose: () => void;
  onTargetChange: (target: ImportTarget | null) => void;
  onSetDataValue: (key: string, value: string) => void;
  /** Called once the markdown has been handed to the assistant. */
  onSent?: () => void;
};

type Mode = 'new' | 'existing';

const REQUIRED_NEW_KEYS = ['order.transaction_type', 'order.type', 'order.workflow'] as const;

// The first line for a key is the one upsertDataLine edits, so the form and the markdown always agree.
const valueOf = (lines: readonly MarkdownLine[], key: string): string => (
  lines.find((line) => line.key === key && line.status !== 'malformed')?.value ?? ''
);

const modeFromRadio = (value: string): Mode | null => {
  if (value === 'new' || value === 'existing') return value;
  return null;
};

const useStyles = makeStyles((theme: Theme) => createStyles({
  sendButton: {
    textTransform: 'none',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  note: {
    color: theme.palette.text.secondary,
  },
  messages: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  validation: {
    color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark,
  },
  radio: theme.palette.mode === 'dark' ? {
    '&&.Mui-checked': { color: theme.palette.text.primary },
  } : {},
}));

export const TargetAndHandoff = ({
  markdown, parsed, open, onClose, onTargetChange, onSetDataValue, onSent,
}: TargetAndHandoffProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation('imports');
  const { currentUser } = useUserInformationContext();
  const {
    isServiceReady,
    pendingAssistantMessage,
    queueAssistantMessage,
    openAssistantDrawer,
    llmServiceRef,
    triggerClearConversation,
  } = useLLMProxyServiceContext();

  const accesses = currentUser?.accesses ?? [];
  const [pendingMode, setPendingMode] = useState<Mode | null>(null);
  const [agencyChoice, setAgencyChoice] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);
  const [blockedBusy, setBlockedBusy] = useState(false);
  const sendingRef = useRef(false);

  const agencyId = agencyChoice || (accesses.length === 1 ? accesses[0]._id : '');
  const mode: Mode | null = parsed.target?.kind ?? pendingMode;
  const targetId = parsed.target?.kind === 'existing' ? parsed.target.id : '';

  const newValues = {
    transaction_type: valueOf(parsed.lines, 'order.transaction_type'),
    type: valueOf(parsed.lines, 'order.type'),
    workflow: valueOf(parsed.lines, 'order.workflow'),
  };
  const newComplete = parsed.target?.kind === 'new'
    && REQUIRED_NEW_KEYS.every((key) => valueOf(parsed.lines, key) !== '')
    && (!agencyChoiceNeeded(accesses.length) || agencyId !== '');
  const targetComplete = newComplete || parsed.target?.kind === 'existing';
  const serviceAndDataReady = isServiceReady && parsed.lines.some((line) => line.status !== 'malformed');
  const canSend = targetComplete && serviceAndDataReady && pendingAssistantMessage === null;

  const selectMode = (next: Mode) => {
    setPendingMode(next);
    if (next === 'new') {
      onTargetChange({ kind: 'new' });
      return;
    }
    if (parsed.target?.kind !== 'existing') onTargetChange(null);
  };

  const pickOrder = (order: PickedOrder) => {
    onTargetChange({ kind: 'existing', id: order._id });
  };

  const closeDialog = () => {
    setBlockedBusy(false);
    onClose();
  };

  // A new import starts a clean conversation: the open one is cleared first, after the reviewer agrees.
  const sendToAssistant = async (target: ImportTarget) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    try {
      await triggerClearConversation();
      queueAssistantMessage(buildAssistantInstruction({
        markdown,
        target,
        timezoneOffset: formatTimezoneOffset(new Date()),
        agencyId: target.kind === 'new' ? agencyId : null,
      }));
      openAssistantDrawer();
      onSent?.();
      closeDialog();
    } finally {
      sendingRef.current = false;
    }
  };

  const handleSend = () => {
    const { target } = parsed;
    if (!canSend || target === null) return;
    const service = llmServiceRef.current;
    if (service?.isBusy()) {
      // Clearing while an answer is streaming would be deferred into the old thread, so wait instead.
      setBlockedBusy(true);
      return;
    }
    setBlockedBusy(false);
    if (service?.hasConversation()) {
      setConfirmClear(true);
      return;
    }
    sendToAssistant(target);
  };

  const confirmSend = () => {
    setConfirmClear(false);
    if (parsed.target !== null) sendToAssistant(parsed.target);
  };

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      aria-labelledby="send-to-assistant-title"
    >
      <DialogTitle id="send-to-assistant-title">{t('imports:target.title')}</DialogTitle>
      <DialogContent className={classes.content}>
        <RadioGroup
          row
          aria-label={t('imports:target.title')}
          value={mode ?? ''}
          onChange={(event) => {
            const next = modeFromRadio(event.target.value);
            if (next !== null) selectMode(next);
          }}
        >
          <FormControlLabel value="new" control={<Radio color="primary" className={classes.radio} />} label={t('imports:target.new')} />
          <FormControlLabel value="existing" control={<Radio color="primary" className={classes.radio} />} label={t('imports:target.existing')} />
        </RadioGroup>

        {mode === 'new' && (
          <NewOrderFields
            values={newValues}
            agencyId={agencyId}
            onSetValue={onSetDataValue}
            onAgencyChange={setAgencyChoice}
          />
        )}
        {mode === 'existing' && (
          <ExistingOrderSearch
            targetId={targetId}
            agencyId={agencyId || undefined}
            onSelect={pickOrder}
          />
        )}

        <Typography variant="body2" className={classes.note}>
          {t('imports:handoff.note')}
        </Typography>

        <div className={classes.messages}>
          {!targetComplete && (
            <Typography variant="caption" className={classes.validation}>{t('imports:target.incomplete')}</Typography>
          )}
          {!serviceAndDataReady && (
            <Typography variant="caption" className={classes.validation}>{t('imports:handoff.notReady')}</Typography>
          )}
          {blockedBusy && (
            <Alert severity="warning">{t('imports:send.busy')}</Alert>
          )}
        </div>
      </DialogContent>
      <ModalActionBar
        onCancel={closeDialog}
        submitType="button"
        submitText={t('imports:handoff.button')}
        submitIcon="paper-plane"
        onSubmit={handleSend}
        submitDisabled={!canSend}
      />
      <SendConfirmDialog
        open={confirmClear}
        onConfirm={confirmSend}
        onCancel={() => setConfirmClear(false)}
      />
    </Dialog>
  );
};